import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import PartnerLayout from "@/components/partner/PartnerLayout";
import { PartnerDataProvider, usePartnerData } from "@/hooks/usePartnerData";
import { useAuth } from "@/contexts/AuthContext";

type PageMeta = {
  title: string;
  subtitle?: string;
  action?: ReactNode;
};

type PageMetaContextValue = {
  setPageMeta: (meta: PageMeta) => void;
};

const PageMetaContext = createContext<PageMetaContextValue | null>(null);

const ROUTE_META: Record<string, { title: string; subtitle?: string }> = {
  "/partners/dashboard": {
    title: "Home",
    subtitle: "Your recruitment queue — applications, deadlines, and messages",
  },
  "/partners/programs": {
    title: "Programs",
    subtitle: "Listings that appear for students in Discover",
  },
  "/partners/programs/new": {
    title: "Publish program",
    subtitle: "Create a listing for high school students on Discover",
  },
  "/partners/opportunities": {
    title: "Programs",
    subtitle: "Listings that appear for students in Discover",
  },
  "/partners/opportunities/new": {
    title: "Publish program",
    subtitle: "Create a listing for high school students on Discover",
  },
  "/partners/applications": {
    title: "Applications",
    subtitle: "Search, filter, and review applicants quickly",
  },
  "/partners/messages": {
    title: "Messages",
    subtitle: "Conversations with students about your listings",
  },
  "/partners/analytics": {
    title: "Reach",
    subtitle: "Views, applications, and saves on your listings",
  },
  "/partners/organization": {
    title: "Organization",
    subtitle: "Public profile students see on Discover",
  },
  "/partners/team": {
    title: "Team",
    subtitle: "Roles and collaboration",
  },
  "/partners/settings": {
    title: "Settings",
    subtitle: "Account and security",
  },
  "/partners/integrations": {
    title: "Integrations",
    subtitle: "Google Calendar and Meet",
  },
  "/partners/billing": {
    title: "Billing",
  },
  "/partners/manage-campaign": {
    title: "Manage Campaign",
  },
  "/partners/upgrade": {
    title: "Upgrade",
  },
};

function resolveRouteMeta(pathname: string) {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];
  if (pathname.startsWith("/partners/programs/edit/") || pathname.startsWith("/partners/opportunities/edit/")) {
    return {
      title: "Edit program",
      subtitle: "Update details students see in Discover",
    };
  }
  if (pathname.startsWith("/partners/programs/view/") || pathname.startsWith("/partners/opportunities/view/")) {
    return {
      title: "Program",
      subtitle: "How this listing appears to students",
    };
  }
  return { title: "Partner workspace" };
}

/** Pages can override title/subtitle/action without remounting the shell. */
export function usePartnerPageMeta(meta: PageMeta) {
  const ctx = useContext(PageMetaContext);
  useEffect(() => {
    ctx?.setPageMeta(meta);
    // Route change clears overrides in PartnerShellFrame, no cleanup needed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.title, meta.subtitle, meta.action]);
}

function PartnerShellFrame() {
  const location = useLocation();
  const { user } = useAuth();
  const { applications, messages } = usePartnerData();
  const defaults = useMemo(
    () => resolveRouteMeta(location.pathname),
    [location.pathname]
  );
  const [override, setOverride] = useState<PageMeta | null>(null);

  // Reset overrides when the route changes so stale actions don't linger
  useEffect(() => {
    setOverride(null);
  }, [location.pathname]);

  const title = override?.title || defaults.title;
  const subtitle = override?.subtitle ?? defaults.subtitle;
  const action = override?.action;

  return (
    <PageMetaContext.Provider value={{ setPageMeta: setOverride }}>
      <PartnerLayout
        title={title}
        subtitle={subtitle}
        action={action}
        badgeCounts={{
          // New applications only (pending). Clears when opened → reviewing.
          applications: applications.filter(
            (a) => a.status === "pending" || a.status === "new"
          ).length,
          // Incoming unread only (not messages we sent).
          messages: messages.filter(
            (m) => !m.is_read && m.sender_id !== user?.id && m.receiver_id === user?.id
          ).length,
        }}
      >
        <Outlet />
      </PartnerLayout>
    </PageMetaContext.Provider>
  );
}

/**
 * Shared partner shell: data loads once, sidebar stays mounted across tabs.
 */
export default function PartnerWorkspaceShell() {
  return (
    <PartnerDataProvider>
      <PartnerShellFrame />
    </PartnerDataProvider>
  );
}
